import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import { FORM_ERROR } from "final-form";
import Field from "components/Field";
import SelectSku from "components/SelectSku";
import useGlobalContext from "hooks/globalData";
import { useMemo } from "react";
import { withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import translateError from "utils/translateError";

type MoveSkuRegistrationsFormValues = {
  targetSkuId: string | null;
  expireSourceRegistrations: boolean;
};

const { Form } = withTypes<MoveSkuRegistrationsFormValues>();

type MoveSkuRegistrationsDialogProps = {
  sourceSkuId: string;
  open: boolean;
  close: () => void;
};

export default function MoveSkuRegistrationsDialog({
  sourceSkuId,
  open,
  close,
}: MoveSkuRegistrationsDialogProps) {
  const { formatMessage: t } = useIntl();
  const { userSkuService, skuService } = useGlobalContext();
  const queryClient = useQueryClient();

  const { data: sourceSkuData } = useQuery(
    ["sku", sourceSkuId],
    () => skuService.getSkuById(sourceSkuId),
    {
      enabled: open && !!sourceSkuId,
    }
  );

  const init = useMemo<MoveSkuRegistrationsFormValues>(
    () => ({
      targetSkuId: null,
      expireSourceRegistrations: false,
    }),
    []
  );

  const moveSkuRegistrations = async (
    values: MoveSkuRegistrationsFormValues
  ) => {
    try {
      await userSkuService.moveSkuRegistrations(
        sourceSkuId,
        values.targetSkuId!,
        values.expireSourceRegistrations
      );

      queryClient.invalidateQueries(["sku-registrations", sourceSkuId]);
      close();
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>
        <T id="moveSkuRegistrations" />
      </DialogTitle>
      <Form
        onSubmit={moveSkuRegistrations}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError, values }) => {
          return (
            <form onSubmit={handleSubmit} noValidate>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <T id="moveSkuRegistrationsInfo" />
                    </Alert>
                  </Grid>

                  <Field name="targetSkuId" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <SelectSku
                          fullWidth
                          variant="standard"
                          label={t({ id: "targetSkuLabel" })}
                          organizationId={
                            sourceSkuData?.data?.ownerOrganizationId
                          }
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                            helperText:
                              meta.error && meta.submitFailed && meta.error,
                          }}
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="expireSourceRegistrations" type="checkbox">
                    {({ input }) => (
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={input.value}
                              onChange={input.onChange}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              <T id="expireSourceSkuRegistrations" />
                            </Typography>
                          }
                        />
                      </Grid>
                    )}
                  </Field>

                  {submitError && (
                    <Grid item xs={12}>
                      <Alert severity="error">{submitError}</Alert>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button disabled={submitting} onClick={close}>
                  {t({ id: "cancelButton" })}
                </Button>

                <Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  disabled={submitting}
                  startIcon={submitting && <CircularProgress size={16} />}
                >
                  {t({ id: "saveButton" })}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      />
    </Dialog>
  );
}
